import { useEffect, useMemo, useState } from "react";
import { avatarColor, formatListTime, initials } from "../lib/format";
import { useLocale, useT } from "../lib/useT";
import { useContextMenu } from "../lib/useContextMenu";
import type { SessionRow } from "../openclaw/types";
import {
  conversationActivityAt,
  conversationTitle,
  isEmptyCronRow,
  useStore,
  type LiveRun,
} from "../state/store";
import { IconCompose, IconPin, IconSearch, IconSettings } from "./icons";

function ConversationRow({
  row,
  isActive,
  isBusy,
  livePreview,
  onOpen,
  onMenu,
}: {
  row: SessionRow;
  isActive: boolean;
  isBusy: boolean;
  livePreview?: string;
  onOpen: () => void;
  onMenu: () => void;
}) {
  const contextMenu = useContextMenu(onMenu);
  const t = useT();
  const locale = useLocale();
  const title = conversationTitle(row);
  const preview = isBusy
    ? livePreview?.trim()
      ? livePreview.replace(/\s+/g, " ").slice(0, 120)
      : t("list.working")
    : (row.lastMessagePreview ?? "").replace(/\s+/g, " ");

  return (
    <button
      type="button"
      className="conversation-row"
      aria-current={isActive}
      onContextMenu={contextMenu.onContextMenu}
      onPointerDown={contextMenu.onPointerDown}
      onPointerMove={contextMenu.onPointerMove}
      onPointerUp={contextMenu.onPointerUp}
      onPointerCancel={contextMenu.onPointerCancel}
      onClick={(event) => {
        // A long-press already opened the menu; the trailing click must not
        // also open the conversation behind it.
        if (contextMenu.onClick(event)) {
          onOpen();
        }
      }}
    >
      <span className="avatar" style={{ background: avatarColor(row.key) }} aria-hidden="true">
        {initials(title)}
        {isBusy ? <span className="avatar-busy" /> : null}
      </span>
      <span className="conversation-body">
        <span className="conversation-line">
          <span className="conversation-title">{title}</span>
          {row.pinned ? (
            <span className="pin-marker" aria-label={t("list.pinned")}>
              <IconPin />
            </span>
          ) : null}
          <span className="conversation-time">
            {formatListTime(conversationActivityAt(row), locale, t)}
          </span>
        </span>
        <span className={`conversation-preview${isBusy ? " is-busy" : ""}`}>
          {preview || t("list.noMessages")}
        </span>
      </span>
    </button>
  );
}

export function ConversationList({
  onCompose,
  onSettings,
  onRowMenu,
}: {
  onCompose: () => void;
  onSettings: () => void;
  onRowMenu: (key: string) => void;
}) {
  const conversations = useStore((state) => state.conversations);
  const activeKey = useStore((state) => state.activeKey);
  const liveRuns = useStore((state) => state.liveRuns);
  const loading = useStore((state) => state.conversationsLoading);
  const listError = useStore((state) => state.listError);
  const scope = useStore((state) => state.scope);
  const setScope = useStore((state) => state.setScope);
  const openConversation = useStore((state) => state.openConversation);
  const search = useStore((state) => state.search);
  const searchMessages = useStore((state) => state.searchMessages);
  const [query, setQuery] = useState("");
  const t = useT();
  const locale = useLocale();

  // The title filter is instant and local; the message search is a round trip,
  // so it waits for a pause in the typing.
  useEffect(() => {
    const handle = setTimeout(() => void searchMessages(query), 250);
    return () => clearTimeout(handle);
  }, [query, searchMessages]);

  // Empty cron sessions are noise in the default view but still real rows, so
  // a search reaches them and a counter says how many are being held back.
  const [showEmptyCron, setShowEmptyCron] = useState(false);

  const { visible, hiddenCount } = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle) {
      return {
        visible: conversations.filter((row) => {
          const haystack =
            `${conversationTitle(row)} ${row.lastMessagePreview ?? ""}`.toLowerCase();
          return haystack.includes(needle);
        }),
        hiddenCount: 0,
      };
    }
    if (showEmptyCron) {
      return { visible: conversations, hiddenCount: 0 };
    }
    const kept = conversations.filter((row) => !isEmptyCronRow(row));
    return { visible: kept, hiddenCount: conversations.length - kept.length };
  }, [conversations, query, showEmptyCron]);

  function isBusy(row: SessionRow, run: LiveRun | undefined): boolean {
    return Boolean(run?.active || row.hasActiveRun || row.status === "running");
  }

  return (
    <div className="pane-list">
      <header className="header">
        <div className="header-title">
          <h1>{t("list.title")}</h1>
        </div>
        <button
          type="button"
          className="icon-button"
          onClick={onSettings}
          aria-label={t("list.settings")}
          title={t("list.settings")}
        >
          <IconSettings />
        </button>
        <button
          type="button"
          className="icon-button is-primary"
          onClick={onCompose}
          aria-label={t("list.compose")}
          title={t("list.compose")}
        >
          <IconCompose />
        </button>
      </header>

      <div className="list-tools">
        <div className="search-field">
          <IconSearch />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("list.search")}
            type="search"
            aria-label={t("list.searchLabel")}
          />
        </div>
      </div>

      <div className="scope-tabs">
        <button
          type="button"
          className="scope-tab"
          aria-pressed={scope === "active"}
          onClick={() => setScope("active")}
        >
          {t("list.scopeActive")}
        </button>
        <button
          type="button"
          className="scope-tab"
          aria-pressed={scope === "archived"}
          onClick={() => setScope("archived")}
        >
          {t("list.scopeArchived")}
        </button>
      </div>

      {listError ? <div className="banner is-danger">{listError}</div> : null}

      <div className="conversation-list">
        {visible.map((row) => (
          <ConversationRow
            key={row.key}
            row={row}
            isActive={row.key === activeKey}
            isBusy={isBusy(row, liveRuns[row.key])}
            livePreview={liveRuns[row.key]?.text}
            onOpen={() => void openConversation(row.key)}
            onMenu={() => onRowMenu(row.key)}
          />
        ))}

        {hiddenCount || (showEmptyCron && !query) ? (
          <button
            type="button"
            className="cron-toggle"
            onClick={() => setShowEmptyCron((current) => !current)}
          >
            {showEmptyCron
              ? t("list.cronShown")
              : hiddenCount === 1
                ? t("list.cronHiddenOne")
                : t("list.cronHiddenMany", { count: hiddenCount })}
          </button>
        ) : null}

        {/* Hits from the transcripts themselves, below the matching chats. The
            list filter only ever sees titles and the loaded page. */}
        {search.query && (search.hits.length || search.loading || search.indexing) ? (
          <div className="search-hits">
            <div className="search-hits-head">
              {t("list.inMessages")}
              {search.loading ? <span className="spinner" /> : null}
            </div>
            {search.indexing && !search.hits.length ? (
              <p className="search-hits-note">
                {t("list.indexing")}
              </p>
            ) : null}
            {search.hits.map((hit) => {
              const row = conversations.find((entry) => entry.key === hit.sessionKey);
              return (
                <button
                  type="button"
                  className="search-hit"
                  key={`${hit.sessionKey}:${hit.messageId}`}
                  onClick={() => void openConversation(hit.sessionKey)}
                >
                  <span className="search-hit-line">
                    <span className="search-hit-title">
                      {row ? conversationTitle(row) : hit.sessionKey}
                    </span>
                    <span className="search-hit-time">{formatListTime(hit.timestamp, locale, t)}</span>
                  </span>
                  <span className="search-hit-snippet">
                    {hit.role === "user" ? t("list.fromYou") : ""}
                    {hit.snippet}
                  </span>
                </button>
              );
            })}
          </div>
        ) : null}

        {!visible.length && !search.hits.length ? (
          <div className="empty-state">
            {loading ? (
              <>
                <span className="spinner" />
                <p>{t("list.loading")}</p>
              </>
            ) : query ? (
              <>
                <span className="empty-icon" aria-hidden="true">
                  🔍
                </span>
                <h2>{t("list.noMatchTitle")}</h2>
                <p>{t("list.noMatchBody", { query })}</p>
              </>
            ) : scope === "archived" ? (
              <>
                <span className="empty-icon" aria-hidden="true">
                  🗄️
                </span>
                <h2>{t("list.archiveEmptyTitle")}</h2>
                <p>{t("list.archiveEmptyBody")}</p>
              </>
            ) : (
              <>
                <span className="empty-icon" aria-hidden="true">
                  💬
                </span>
                <h2>{t("list.emptyTitle")}</h2>
                <p>{t("list.emptyBody")}</p>
              </>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
